import { useState } from "preact/hooks"
import { useDropzone } from "react-dropzone"
import { toast } from "../../../components/ui/use-toast"
import { Button } from "../../../components/ui/button"
import { Label } from "../../../components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select"
import { Input } from "../../../components/ui/input"
import { Upload, Loader2, FileText, X } from "lucide-react"
import { format } from "date-fns"
import { uploadDistributorReport } from "../api/distributor_sales_api"
import { DistributorSource, ReportType, UploadResponse, ValuationMethod } from "../types/distributor_sales"

interface DistributorSalesUploadProps {
  className?: string
  onUploadComplete?: (response: UploadResponse) => void | Promise<void>
  /** When set, source is fixed (e.g. KeHE-only on Sales Analysis) and the source picker is hidden. */
  sourceLocked?: DistributorSource
}

export function DistributorSalesUpload({ className, onUploadComplete, sourceLocked }: DistributorSalesUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [source, setSource] = useState<DistributorSource | "">(sourceLocked ?? "")
  const [reportType, setReportType] = useState<ReportType | "">("")
  const [valuationMethod, setValuationMethod] = useState<ValuationMethod | "">("")
  const [reportDate, setReportDate] = useState<string>(format(new Date(), "yyyy-MM-dd"))
  const [startDate, setStartDate] = useState<string>("")
  const [endDate, setEndDate] = useState<string>("")
  const [uploadBrandName, setUploadBrandName] = useState<string>("")


  async function handleUpload() {
    if (!selectedFile || !source || !reportDate) {
      toast({
        title: "Missing Information",
        description: sourceLocked
          ? "Please select a file and report date."
          : "Please select a file, source, and report date.",
        variant: "destructive",
      })
      return
    }

    setUploading(true)
    try {
      const response = await uploadDistributorReport(
        selectedFile, 
        source, 
        reportDate,
        startDate || undefined,
        endDate || undefined,
        reportType || undefined,
        valuationMethod || undefined,
        uploadBrandName || undefined
      )
      toast({
        title: "Report Uploaded",
        description: `Successfully imported ${response.rows_imported} rows from ${response.source.toUpperCase()}.`,
      })
      setSelectedFile(null)
      setSource(sourceLocked ?? "")
      setReportType("")
      setValuationMethod("")
      setUploadBrandName("")
      setStartDate("")
      setEndDate("")
      await onUploadComplete?.(response)
    } catch (error) {
      console.error("Error uploading report:", error)
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "Failed to upload report. Please try again.",
        variant: "destructive",
      })
    } finally {
      setUploading(false)
    }
  }

  async function handleDrop(files: File[]) {
    if (files.length > 0) {
      const file = files[0]
      setSelectedFile(file)
      
      const fileName = file.name.toLowerCase()
      const isSpreadsheet = fileName.endsWith(".xlsx") || fileName.endsWith(".xls")
      
      // Read text content for auto-detection. Excel workbooks are binary zips; decoding them
      // as text can stall large syndicated uploads before the request is sent.
      let fileContent = ""
      if (!isSpreadsheet) {
        try {
          fileContent = await file.text()
        } catch (err) {
          // If we can't read, we'll just use filename-based detection
        }
      }
      const contentLower = fileContent.toLowerCase()
      
      // Auto-detect source and report type from filename or headers
      if (isMcLaneSalesContent(contentLower) || (sourceLocked === "mclane" && isSpreadsheet)) {
        setSource("mclane")
        setReportType("")
        setValuationMethod("")
      } else if (isBrandWeeklyMovementContent(contentLower)) {
        setSource("spins")
        setReportType("brand_weekly_movement")
        setValuationMethod("")
        setUploadBrandName("")
        const latestDate = latestBrandMovementDate(fileContent)
        if (latestDate) {
          setReportDate(latestDate)
          setEndDate(latestDate)
        }
      } else if (fileName.includes("spins") || contentLower.includes("dollars, yago") || contentLower.includes("avg % acv")) {
        setSource("spins")
        setReportType("yago_comparison")
        setValuationMethod("")
        setUploadBrandName("")
        const dateMatch = fileContent.match(/Weeks End (\d{2})\/(\d{2})\/(\d{4})/)
        if (dateMatch) {
          setReportDate(`${dateMatch[3]}-${dateMatch[1]}-${dateMatch[2]}`)
          setEndDate(`${dateMatch[3]}-${dateMatch[1]}-${dateMatch[2]}`)
        }
      } else if (fileName.includes("chargeback")) {
        setSource("kehe")
        setReportType("chargebacks_by_chain")
        setValuationMethod("")
      } else if (fileName.includes("kehe") || (fileName.includes("chains") && fileName.includes("stores"))) {
        // KeHE vendor sales (Chains and Stores format)
        setSource("kehe")
        setReportType("vendor_sales")
        
        // Auto-detect valuation method from filename or content
        detectValuationMethod(fileName, contentLower, fileContent)
      } else if (!sourceLocked && fileName.includes("unfi")) {
        setSource("unfi")
        setReportType("chains_and_stores")
        setValuationMethod("")
      } else if (fileName.includes("chains") && fileName.includes("stores")) {
        // KeHE Chains & Stores when source is locked to KeHE; otherwise infer from content
        if (sourceLocked === "kehe" || contentLower.includes("retailerparent") || contentLower.includes("storesandsupplier")) {
          if (sourceLocked) setSource(sourceLocked)
          setReportType("vendor_sales")
          detectValuationMethod(fileName, contentLower, fileContent)
        } else {
          setReportType("")
        }
      }
      if (sourceLocked) setSource(sourceLocked)
    }
  }

  function isMcLaneSalesContent(contentLower: string): boolean {
    const header = contentLower.split("\n")[0] ?? ""
    // "McLane Week" in the shipments export, "Last Date of McLaneWeek" in the by-store export.
    return (header.includes("mclane week") || header.includes("mclaneweek")) && header.includes("total last invoice cost")
  }

  function isBrandWeeklyMovementContent(contentLower: string): boolean {
    if (!contentLower) return false
    return [
      "geography",
      "time period",
      "time period end date",
      "product universe",
      "product level",
      "upc",
      "description",
      "base units",
      "incr units",
    ].every((header: string) => contentLower.includes(header))
  }

  function latestBrandMovementDate(contentOriginal: string): string | null {
    const matches = Array.from(contentOriginal.matchAll(/\b(\d{2})\/(\d{2})\/(\d{4})\b/g))
    const dates = matches.map((match: RegExpMatchArray) => `${match[3]}-${match[1]}-${match[2]}`).sort()
    return dates.length > 0 ? dates[dates.length - 1] : null
  }
  
  function detectValuationMethod(fileName: string, contentLower: string, contentOriginal: string) {
    // Check filename first (most reliable explicit indicators)
    if (fileName.includes("list") && fileName.includes("wholesale")) {
      setValuationMethod("list_wholesale")
      return
    }
    if (fileName.includes("vendor") && fileName.includes("cost")) {
      setValuationMethod("vendor_cost")
      return
    }
    if (fileName.includes("quantity") || fileName.includes("eaches") || fileName.includes("units")) {
      setValuationMethod("quantity")
      return
    }
    
    // MOST RELIABLE: Check for dollar signs FIRST (before keyword checks)
    // Dollar signs are the definitive indicator - if present, it's vendor_cost
    if (contentOriginal.includes('$')) {
      setValuationMethod("vendor_cost")
      return
    }
    
    // Check content for valuation hints (use lowercased for text matching)
    // Only check these if no dollar signs found
    if (contentLower.includes("vendor cost") || contentLower.includes("vendorcost") || contentLower.includes("net cost")) {
      setValuationMethod("vendor_cost")
      return
    }
    if (contentLower.includes("quantity") || contentLower.includes("eaches") || contentLower.includes("unit count")) {
      setValuationMethod("quantity")
      return
    }
    
    // Default to quantity (eaches) if no dollar signs found
    setValuationMethod("quantity")
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
  })

  return (
    <div class={`bg-white border border-gray-200 rounded-lg p-6 ${className || ""}`}>
        <h3 class="text-lg font-semibold mb-4">
          {sourceLocked === "kehe"
            ? "Upload KeHE Sales Report"
            : sourceLocked === "mclane"
              ? "Upload McLane Sales Report"
              : "Upload Distributor Sales Report"}
        </h3>
      
      {/* Drag & Drop Area */}
      <div
        {...(getRootProps() as any)}
        class={`border-2 border-dashed rounded-lg p-6 text-center transition-colors mb-4 ${
          isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
        }`}
      >
        <input {...getInputProps()} />
        
        {selectedFile ? (
          <div class="flex items-center justify-center gap-3">
            <FileText className="h-8 w-8 text-blue-500" />
            <div class="text-left">
              <p class="font-medium text-gray-900">{selectedFile.name}</p>
              <p class="text-sm text-gray-500">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <button
              onClick={(e: Event) => {
                e.stopPropagation()
                setSelectedFile(null)
              }}
              class="p-1 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>
        ) : (
          <>
            <Upload className="mx-auto h-10 w-10 text-gray-400 mb-3" />
            <p class="text-sm text-gray-600 mb-2">
              {isDragActive ? "Drop file here" : "Drag & drop a CSV or Excel file"}
            </p>
            <Button
              onClick={open}
              variant="outline"
              // @ts-ignore
              size="sm"
            >
              Select File
            </Button>
          </>
        )}
      </div>

      {/* Form Fields */}
      <div class={`grid gap-4 mb-4 ${sourceLocked ? "grid-cols-1" : "grid-cols-2"}`}>
        {!sourceLocked ? (
          <div>
            <Label class="mb-2 block">Source</Label>
            <Select
              value={source}
              onValueChange={(value: string) => {
                setSource(value as DistributorSource)
                setReportType(value === "spins" ? "yago_comparison" : "")
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select source..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="kehe">KeHE</SelectItem>
                <SelectItem value="unfi">UNFI</SelectItem>
                <SelectItem value="spins">Syndicated Data</SelectItem>
                <SelectItem value="mclane">McLane</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : null}

        {source !== "mclane" ? (
        <div>
          <Label class="mb-2 block">Report Date</Label>
          <Input
            type="date"
            value={reportDate}
            onChange={(e: Event) => setReportDate((e.target as HTMLInputElement).value)}
          />
        </div>
        ) : null}
      </div>

      {source === "mclane" ? (
        <div class="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <p class="font-medium">McLane sales export</p>
          <p class="mt-1">
            Upload the McLane export with Division Name, Customer Group, Customer, Item, McLane Week, Total Last
            Invoice Cost and Total Consumer Units, or the by-store export (CSV or XLSX) that adds Customer Name
            Address, which fills in stores and store counts. Dates come from the McLane Week column, and
            re-uploading a week replaces it.
          </p>
        </div>
      ) : source === "spins" ? (
        <>
          <div class="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
            <p class="font-medium">Supported syndicated data files</p>
            <p class="mt-1">
              Upload SPINS/YAGO comparison reports with year-ago, ACV, TDP, promo, and distribution columns, or brand weekly movement CSVs with Geography, Time Period End Date, UPC, Description, Dollars, Units, Base Units, and Incr Units.
            </p>
            {reportType && (
              <p class="mt-2 text-xs font-medium uppercase tracking-wide text-blue-700">
                Detected: {reportType === "brand_weekly_movement" ? "Brand weekly movement" : "SPINS/YAGO comparison"}
              </p>
            )}
          </div>
          <div class="mb-4">
            <Label class="mb-2 block">Brand in Syndicated Data</Label>
            <Input
              value={uploadBrandName}
              placeholder="Defaults to this brand account..."
              onChange={(e: Event) => setUploadBrandName((e.target as HTMLInputElement).value)}
            />
            <p class="text-xs text-gray-500 mt-1">
              For YAGO files, use the brand name as it appears in the syndicated report. For weekly movement files, this becomes the brand shown in the dashboard.
            </p>
          </div>
        </>
      ) : (
        <>
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label class="mb-2 block">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e: Event) => setStartDate((e.target as HTMLInputElement).value)}
              />
            </div>
            <div>
              <Label class="mb-2 block">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e: Event) => setEndDate((e.target as HTMLInputElement).value)}
              />
            </div>
          </div>
          <p class="text-xs text-gray-500 mb-4 -mt-2">
            Specify the date range the report data covers (optional)
          </p>
        </>
      )}

      {/* Report Type - show for KeHE since they have multiple formats */}
      {source === "kehe" && (
        <div class="mb-4">
          <Label class="mb-2 block">Report Type</Label>
          <Select
            value={reportType}
            onValueChange={(value: string) => {
              setReportType(value as ReportType)
              // Reset valuation when report type changes
              if (value !== "vendor_sales") {
                setValuationMethod("")
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Auto-detect from file..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="vendor_sales">Vendor Sales (Chains & Stores)</SelectItem>
              <SelectItem value="chargebacks_by_chain">Chargebacks by Chain</SelectItem>
            </SelectContent>
          </Select>
          <p class="text-xs text-gray-500 mt-1">
            Leave blank to auto-detect from file content
          </p>
        </div>
      )}

      {/* Valuation Method - show for KeHE vendor sales */}
      {source === "kehe" && reportType === "vendor_sales" && (
        <div class="mb-4">
          <Label class="mb-2 block">Valuation Method</Label>
          <Select
            value={valuationMethod}
            onValueChange={(value: string) => setValuationMethod(value as ValuationMethod)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Auto-detected..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="vendor_cost">Vendor cost ($)</SelectItem>
              <SelectItem value="list_wholesale">List wholesale ($)</SelectItem>
              <SelectItem value="quantity">Quantity (eaches)</SelectItem>
            </SelectContent>
          </Select>
          <p class="text-xs text-gray-500 mt-1">
            Auto-detected from file. Upload both valuations to see values side-by-side.
          </p>
        </div>
      )}

      {/* Upload Button */}
      <Button
        onClick={handleUpload}
        disabled={!selectedFile || !source || !reportDate || uploading}
        class="w-full"
      >
        {uploading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <Upload className="h-4 w-4 mr-2" />
            Upload Report
          </>
        )}
      </Button>

      {/* Help Text */}
      <p class="text-xs text-gray-500 mt-3 text-center">
        {sourceLocked === "kehe"
          ? "Supports KeHE Vendor Sales (Chains & Stores) and Chargebacks by Chain reports."
          : sourceLocked === "mclane"
            ? "Supports McLane weekly shipment exports (CSV or Excel)."
            : "Supports KeHE, UNFI, SPINS/YAGO, McLane, and brand weekly movement syndicated reports."}
      </p>
    </div>
  )
}
